// frontend/src/components/CentrifugeLaserV2.jsx
import React, { useRef, useMemo, useEffect } from "react";
import * as THREE from "three";
import { useFrame } from "@react-three/fiber";
import { EffectComposer, Bloom } from "@react-three/postprocessing";
import useCentrifugeLaserV2Visualizer from "../hooks/useCentrifugeLaserV2Visualizer";

/** Nombre maximal de particules affichées — l'explosion en émet 50. */
const MAX_PARTICULES = 50;

/**
 * Rendu de la Centrifugeuse Laser 2.0 — LE BACKEND POSSÈDE FORME ET MOUVEMENT.
 *
 * Voir docs/DOCTRINE_GEOMETRIE_CANONIQUE.md.
 *
 * ⚠️ Ce composant fabriquait son entropie « dynamique » à partir de curseurs et
 * d'un tirage :
 *
 *     dynEntropy = speedFactor * (2.0 + turboMode * 3.0)
 *                + ringSpeedFactor * (1.0 + turboMode * 2.0)
 *                + (flashRatePct / 100) * (1.5 + turboMode * 1.5)
 *                + (chaos ? 0.5 + turboMode : 0.1)
 *                + Math.random() * (0.05 + turboMode * 0.15);
 *
 * Aucune position n'y entrait. C'était une combinaison de réglages d'interface
 * et de hasard, remontée au parent comme une mesure. Il n'animait par ailleurs
 * que des rotations de GROUPE : les objets ne bougeaient jamais les uns par
 * rapport aux autres, donc il n'y avait rien à mesurer.
 *
 * Le composant rejoue désormais les frames de `/animate`, dérive les vitesses
 * du déplacement réel, et transmet l'instantané tel quel. Glow, flash et bloom
 * restent — ce sont des effets de RENDU, ils n'entrent dans aucun observable
 * (docs/ARBITRAGE_CHAOS_GLOW.md).
 */
function SceneContent({
  running = true,
  chaos = false,
  glow = true,
  speedPct = 50,
  ringSpeedPct = 70,
  flashRatePct = 30,
  bloomIntensity = 1.2,
  bloomRadius = 0.6,
  bloomThreshold = 0.0,
  turboMode = 0,
  onSnapshot = () => {},
}) {
  const groupRef = useRef();
  const ringRef = useRef();
  const meshRefs = useRef([]);
  const particuleRefs = useRef([]);
  const playhead = useRef(0);
  const tRef = useRef(0);

  const { objects, nSpheres, core, arm12h, lecteur } =
    useCentrifugeLaserV2Visualizer({
      speedPct,
      ringSpeedPct,
      turboPct: turboMode * 50, // le parent passe 0..2
    });

  // Géométries partagées : 42 objets persistants pour trois formes.
  const geometries = useMemo(
    () => ({
      sphere: new THREE.SphereGeometry(0.45, 16, 16),
      cube: new THREE.BoxGeometry(0.65, 0.65, 0.65),
      satellite: new THREE.SphereGeometry(0.5, 16, 16),
      bras: new THREE.SphereGeometry(0.7, 16, 16),
      particule: new THREE.SphereGeometry(0.12, 8, 8),
      obstacle: new THREE.SphereGeometry(0.9, 16, 16),
    }),
    []
  );

  useEffect(
    () => () => Object.values(geometries).forEach((g) => g.dispose()),
    [geometries]
  );

  // Couleurs CANONIQUES de `/initial`. Ce sont elles que le snapshot remonte —
  // pas les couleurs affichées, que `glow` réécrit à chaque frame.
  const couleurs = useMemo(() => objects.map((o) => o.color), [objects]);

  const vitesses = useRef([]);
  const particulesRef = useRef([]);
  useEffect(() => {
    vitesses.current = objects.map(() => [0, 0, 0]);
    meshRefs.current = meshRefs.current.slice(0, objects.length);
  }, [objects]);

  useFrame((_, delta) => {
    const dt = Math.min(delta, 0.05);
    tRef.current += dt;
    const t = tRef.current;

    if (running && lecteur) {
      // Rejeu : aucune position n'est calculée ici.
      playhead.current += dt;
      const vue = lecteur.lire(playhead.current);
      if (!vue) return;
      if (vue.bascule || vue.gel) playhead.current = 0;
      const { alpha, dt: frameDt } = vue;

      const A = vue.a.positions;
      const B = vue.b.positions;

      for (let i = 0; i < objects.length; i++) {
        const mesh = meshRefs.current[i];
        const a = A[i];
        const b = B[i];
        if (!mesh || !a || !b) continue;

        mesh.position.set(
          a[0] + (b[0] - a[0]) * alpha,
          a[1] + (b[1] - a[1]) * alpha,
          a[2] + (b[2] - a[2]) * alpha
        );
        vitesses.current[i] = [
          (b[0] - a[0]) / frameDt,
          (b[1] - a[1]) / frameDt,
          (b[2] - a[2]) / frameDt,
        ];
      }

      // --- Particules : population VARIABLE ---
      // On n'interpole pas entre deux frames : leur nombre change d'une frame à
      // l'autre, donc l'indice `i` ne désigne pas la même particule. On affiche
      // la frame courante telle quelle, et on masque les emplacements inutilisés.
      // Population VARIABLE : on affiche la frame courante telle quelle, sans
      // interpoler — l'indice ne désigne pas la même particule d'une frame à
      // l'autre (docs/SPEC_CONTINUATION.md § 6.2).
      const parts = vue.a.particules || [];
      particulesRef.current = parts;
      for (let i = 0; i < MAX_PARTICULES; i++) {
        const mesh = particuleRefs.current[i];
        if (!mesh) continue;
        if (i < parts.length) {
          mesh.visible = true;
          mesh.position.set(parts[i][0], parts[i][1], parts[i][2]);
        } else {
          mesh.visible = false;
        }
      }
    }

    // --- Effets de RENDU ---
    const flash = 0.5 + 0.5 * Math.sin(t * 10 * (flashRatePct / 100));
    const boost = 1.0 + turboMode;
    for (let i = 0; i < meshRefs.current.length; i++) {
      const mesh = meshRefs.current[i];
      if (!mesh) continue;
      const base = couleurs[i];
      if (glow) {
        mesh.material.color.setHSL((Math.sin(t * 0.4 + i * 0.08) + 1) / 2, 0.8, 0.55);
      } else if (base) {
        mesh.material.color.setRGB(base[0], base[1], base[2]);
      }
      mesh.material.emissiveIntensity =
        flashRatePct > 0 ? flash * (flashRatePct / 100) * 2.0 * boost : 0;
    }

    // Anneau central — DÉCORATIF, et c'est la cible visible du curseur Anneau.
    //
    // ⚠️ Il avait disparu de la refonte, ce qui laissait `ringSpeedPct` sans
    // aucun effet visible : mesuré à conditions initiales fixées, l'oscillation
    // du satellite qu'il pilote ne déplace ‖v‖ que de 13,290 à 13,342 sur toute
    // sa course — un curseur mou. Le rétablir lui rend sa fonction d'origine.
    //
    // Il n'entre dans AUCUN observable : c'est un effet de rendu, au même titre
    // que le glow (docs/ARBITRAGE_CHAOS_GLOW.md). Le curseur agit donc sur ce
    // que l'opérateur voit et sur le canal UI, qui porte son intention — pas
    // sur la mesure physique.
    if (ringRef.current) {
      ringRef.current.rotation.y -= dt * (ringSpeedPct / 100) * 3.0 * boost;
      ringRef.current.rotation.x =
        Math.sin(ringRef.current.rotation.y * 0.5) * 0.3;
    }

    // Le chaos reste un effet de CADRE : il secoue le groupe entier, ce qui ne
    // déplace aucun objet les uns par rapport aux autres. Le snapshot lit
    // l'espace objet, il n'en dépend donc pas.
    if (groupRef.current) {
      if (chaos) {
        groupRef.current.rotation.z = Math.sin(t * 0.8) * 0.25;
        groupRef.current.position.x = Math.sin(t * 0.4) * 0.4;
      } else {
        groupRef.current.rotation.z = 0;
        groupRef.current.position.x = 0;
      }
    }

    // --- Instantané pour l'entropie dynamique ---
    // Positions AFFICHÉES, couleurs CANONIQUES, vitesses MESURÉES, et les
    // particules dans un champ séparé. C'est le contrat que fixe l'adaptateur
    // du banc (test_conformite_geometrie.py:_snapshots_centrifuge_v2).
    if (objects.length) {
      onSnapshot({
        positions: meshRefs.current
          .filter(Boolean)
          .map((m) => [m.position.x, m.position.y, m.position.z]),
        colors: couleurs,
        velocities: vitesses.current,
        particules: particulesRef.current,
        n_spheres: nSpheres,
      });
    }
  });

  const bloomFinal = bloomIntensity * (1.0 + turboMode);

  return (
    <group ref={groupRef}>
      {/* Noyau laser, au centre — repère fixe de la scène */}
      {core && (
        <mesh position={core.position}>
          <sphereGeometry args={[core.size * 2, 24, 24]} />
          <meshStandardMaterial
            color={new THREE.Color(0.0, 1.0, 1.0)}
            emissive={new THREE.Color(0.0, 1.0, 1.0)}
            emissiveIntensity={glow ? 2.5 * (1.0 + turboMode) : 0.2}
            transparent
            opacity={0.8}
          />
        </mesh>
      )}

      {/* Anneau central — décoratif, piloté par le curseur Anneau */}
      <mesh ref={ringRef}>
        <torusGeometry args={[2.4, 0.16, 16, 64]} />
        <meshStandardMaterial
          color={new THREE.Color(0.1, 0.9, 1.0)}
          emissive={new THREE.Color(0.1, 0.9, 1.0)}
          emissiveIntensity={glow ? 2.5 * (1.0 + turboMode) : 0.2}
          transparent
          opacity={0.75}
        />
      </mesh>

      {/* Bras 12h — FIXE, et c'est lui que le bras mobile percute */}
      {arm12h && (
        <mesh position={arm12h} geometry={geometries.obstacle}>
          <meshStandardMaterial
            color={new THREE.Color(1.0, 0.85, 0.2)}
            emissive={new THREE.Color(1.0, 0.6, 0.0)}
            emissiveIntensity={glow ? 1.8 : 0.3}
            transparent
            opacity={0.75}
          />
        </mesh>
      )}

      {/* Population persistante : sphères, cubes, satellite, bras mobile */}
      {objects.map((o, i) => (
        <mesh
          key={i}
          ref={(el) => (meshRefs.current[i] = el)}
          position={o.position}
          geometry={
            o.type === "cube"
              ? geometries.cube
              : o.type === "satellite"
              ? geometries.satellite
              : o.type === "arm_6h"
              ? geometries.bras
              : geometries.sphere
          }
        >
          <meshStandardMaterial
            color={new THREE.Color(o.color[0], o.color[1], o.color[2])}
            emissive={new THREE.Color(o.color[0], o.color[1], o.color[2])}
            emissiveIntensity={0.4}
          />
        </mesh>
      ))}

      {/* Particules d'explosion — emplacements préalloués, masqués au repos */}
      {Array.from({ length: MAX_PARTICULES }, (_, i) => (
        <mesh
          key={`p-${i}`}
          ref={(el) => (particuleRefs.current[i] = el)}
          geometry={geometries.particule}
          visible={false}
        >
          <meshStandardMaterial
            color={new THREE.Color(1.0, 0.7, 0.1)}
            emissive={new THREE.Color(1.0, 0.5, 0.0)}
            emissiveIntensity={3.0}
          />
        </mesh>
      ))}

      <EffectComposer>
        <Bloom
          intensity={bloomFinal}
          radius={bloomRadius}
          threshold={bloomThreshold}
        />
      </EffectComposer>
    </group>
  );
}

export default function CentrifugeLaserV2(props) {
  return <SceneContent {...props} />;
}
